//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { UnisonIconType } from '@bvengineering/unison-core';
import React, { useState } from 'react';
import styled, { css } from 'styled-components';

import Button from '@components/atoms/button/Button';
import {
  ActionSheetButtonStyles,
  InvisibleButtonStyles,
} from '@components/atoms/button/buttonStyles';
import Icon from '@components/atoms/icon/Icon';
import {
  BodySecondaryText,
  BodyText,
  CaptionSecondaryText,
} from '@components/atoms/text/Text';
import useActionSheet from '@components/molecules/action-sheet/useActionSheet';
import Popover, {
  Props as PopoverProps,
} from '@components/molecules/popover/Popover';
import { usePopover } from '@components/molecules/popover/usePopover';
import SearchBar, {
  SearchBarProps,
} from '@components/molecules/search-bar/SearchBar';
import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import { isDefined } from '@utils/filterPredicates';

export interface IActionSheetButton {
  caption?: string;
  children?: IActionSheetButton[];
  header?: string;
  iconName?: UnisonIconType;
  isDisabled?: boolean;
  label: string;
  onClick?: (event: React.MouseEvent) => void;
  shouldShowSelectedIcon?: boolean;
  tooltipContent?: string;
  trackingId?: string;
  formatLabel?: () => React.ReactNode;
}

export interface ISearchBarConfig
  extends Pick<
    SearchBarProps,
    'placeholder' | 'visuallyHiddenLabel' | 'trackingIdPrefix'
  > {
  searchFilter?: (
    item: IActionSheetButton,
    searchInputValue: string,
  ) => boolean;
}

export interface IOwnProps extends React.HTMLAttributes<HTMLDivElement> {
  actionSheetConfig: IActionSheetButton[];
  children: React.ReactElement;
  onToggleActionSheet?: (isOpen: boolean) => void;
  searchBarConfig?: ISearchBarConfig;
  noItemsContent?: React.ReactElement;
}

type Props = IOwnProps &
  Omit<
    PopoverProps,
    'shouldShowArrow' | 'shouldOpenOnHover' | 'target' | 'content'
  >;

const defaultSearchFilter = (
  item: IActionSheetButton,
  searchInputValue: string,
) => {
  return [item.label, item.caption].some((val) =>
    val?.toLowerCase().includes(searchInputValue.toLowerCase()),
  );
};

/**
 * The ActionSheet molecule renders a list of buttons in a
 * popover, whose click event handlers / names are powered by
 * a list of IActionSheetButton objects. Each button can define
 * its own click event handler or can be used to expose a nested
 * set of action buttons in-place.
 *
 * In the example below, if the user clicked on the "Second button"
 * we'd trigger the onClick callback for that object. If the user
 * clicked on the "First button", we'd display the `children` buttons
 * in the popover instead, and allow the user to navigate back to the
 * original view by clicking on the header above the button. The header
 * would read 'Override header' because header has been defined. If
 * the value were undefined, it would instead fall back to the label.
 *
 * @example
 * <ActionSheet
 *   actionSheetConfig={ [
 *     {
 *       label: 'First button',
 *       header: 'Override header'
 *       children: [
 *         { label: 'You clicked' },
 *         { label: 'On the' },
 *         { label: 'First button', shouldShowSelectedIcon: true },
 *       ],
 *     }, {
 *       label: 'Second Button',
 *       onClick: () => null,
 *     }
 *   ] }
 * />
 *
 */
export const ActionSheet = (
  {
    actionSheetConfig,
    children,
    className,
    onToggleActionSheet = () => undefined,
    searchBarConfig,
    noItemsContent,
    isDisabled = false,
    ...popoverProps
  }: Props
) => {
  const [
    { activeButton, activeButtonHasParent },
    { navigateToChildSheet, navigateToParentSheet, navigateToRootSheet },
  ] = useActionSheet(actionSheetConfig);

  const [connectPopover, { closePopover }] = usePopover();
  const [searchInputValue, setSearchInputValue] = useState('');

  const toggleSheet = (isOpen: boolean, event: Event) => {
    event.preventDefault();
    event.stopPropagation();
    onToggleActionSheet(isOpen);
    setSearchInputValue('');

    navigateToRootSheet(); // Whenever we open/close the sheet, reset the state
  };

  const handleActionButtonClick = (
    event: React.MouseEvent,
    actionButton: IActionSheetButton,
    idx: number,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (actionButton.onClick) {
      actionButton.onClick(event);
    }
    if (actionButton.children) {
      navigateToChildSheet(idx);
    } else {
      closePopover();
      onToggleActionSheet(false);
    }
  };

  const handleParentButtonClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    navigateToParentSheet();
  };

  const searchBarContent = searchBarConfig && (
    <SearchBarContainer>
      <SearchBar
        {...searchBarConfig}
        handleClear={() => setSearchInputValue('')}
        onChange={setSearchInputValue}
        value={searchInputValue}
      />
    </SearchBarContainer>
  );

  // The header we'll render at the top of the sheet
  const header = activeButton.header || activeButton.label;
  const headerContent = header && (
    <>
      <ActionSheetButton
        onClick={(event) => handleParentButtonClick(event)}
        leftContent={
          <>
            {activeButtonHasParent && (
              <InvisibleButton
                onClick={(event) => handleParentButtonClick(event)}
                buttonStyles={InvisibleButtonStyles}
              >
                <ParentNavIcon />
              </InvisibleButton>
            )}
          </>
        }
      >
        {header}
      </ActionSheetButton>
      <ActionDivider />
    </>
  );

  const renderActionButton = (
    actionButton: IActionSheetButton,
    idx: number,
  ) => {
    return (
      <ActionSheetButton
        hasCaption={isDefined(actionButton.caption)}
        // Original designs of this component included padding around the button rather than within the button
        // this is not desired for normal use cases because it reduces the button click target area, but the search
        // type ahead requires these styles
        // TODO: PD-214388 - Rebuild this component so we don't have this requirement
        // and so we dont have buttons nested within buttons
        shouldButtonHaveExternalPadding={isDefined(searchBarConfig)}
        isDisabled={actionButton.isDisabled}
        iconName={actionButton.iconName}
        key={idx}
        onClick={(event) => handleActionButtonClick(event, actionButton, idx)}
        rightContent={
          <>
            {actionButton.children && (
              <InvisibleButton
                onClick={(event) =>
                  handleActionButtonClick(event, actionButton, idx)
                }
                buttonStyles={InvisibleButtonStyles}
              >
                <ChildNavIcon />
              </InvisibleButton>
            )}
            {actionButton.shouldShowSelectedIcon && (
              <InvisibleButton
                onClick={(event) =>
                  handleActionButtonClick(event, actionButton, idx)
                }
                buttonStyles={InvisibleButtonStyles}
              >
                <SelectedIcon />
              </InvisibleButton>
            )}
          </>
        }
        trackingId={actionButton.trackingId}
      >
        <LabelContainer>
          <StyledBodyText ellipsize={2} isDisabled={actionButton.isDisabled}>
            {isDefined(actionButton.formatLabel)
              ? actionButton.formatLabel()
              : actionButton.label}
          </StyledBodyText>
          {actionButton.caption && (
            <CaptionText ellipsize={4}>{actionButton.caption}</CaptionText>
          )}
        </LabelContainer>
      </ActionSheetButton>
    );
  };

  const renderActionButtonWithTooltip = (
    actionButton: IActionSheetButton,
    idx: number,
  ) => {
    return (
      <SmallTooltip
        key={`${actionButton.tooltipContent}${idx}`}
        content={
          <BodySecondaryText>{actionButton.tooltipContent}</BodySecondaryText>
        }
        placement={'left-start'}
        target={renderActionButton(actionButton, idx)}
      />
    );
  };

  const filteredItems = isDefined(searchBarConfig)
    ? activeButton.children?.filter((item) =>
        (searchBarConfig.searchFilter ?? defaultSearchFilter)(
          item,
          searchInputValue,
        ),
      )
    : activeButton.children;

  const contents = (
    <ContentsWrapper
      className={className}
      onClick={(event) => {
        // we want to stop click event propagation from the internal buttons because they can trigger other actions like
        // opening UIA
        event.stopPropagation();
      }}
    >
      {(activeButton.children ?? []).length > 0 ? (
        <div>
          {searchBarContent}
          {headerContent}
          {isDefined(searchBarConfig) && filteredItems?.length === 0 ? (
            <EmptySearchText>No results match your search.</EmptySearchText>
          ) : (
            filteredItems?.map((item, idx) =>
              isDefined(item.tooltipContent) && item.isDisabled
                ? renderActionButtonWithTooltip(item, idx)
                : renderActionButton(item, idx),
            )
          )}
        </div>
      ) : (
        <NoItemsContainer>{noItemsContent}</NoItemsContainer>
      )}
    </ContentsWrapper>
  );

  return (
    <Popover
      connectPopover={connectPopover}
      content={contents}
      isDisabled={isDisabled}
      onTogglePopover={toggleSheet}
      shouldOpenOnHover={false}
      shouldShowArrow={false}
      target={children}
      {...popoverProps}
    />
  );
};

const InvisibleButton = styled(Button)(
  () => `
  height: 100%;
  min-height: auto;
  min-width: auto;
  padding: 0;
  display: flex;
  align-items: center;
  outline: none;

  && svg {
    /* Visually vertically center the icon */
    position: relative;
    top: 0.5px;
  }
`,
);

const ChildNavIcon = styled(Icon).attrs({
  name: 'chevron-right',
})`
  float: right;
`;

const ParentNavIcon = styled(Icon).attrs({
  name: 'chevron-left',
})(
  ({ theme }) => `
  margin-right: ${theme.spacings[3]};
`,
);

const SelectedIcon = styled(Icon).attrs({
  name: 'checkmark',
})(
  ({ theme }) => `
  color: ${theme.colors.green7};
  float: right;
`,
);

const ActionDivider = styled.hr`
  margin: 0;
`;

export const ActionSheetButton = styled(Button).attrs({
  buttonStyles: ActionSheetButtonStyles,
})<{
  hasCaption?: boolean;
  shouldButtonHaveExternalPadding?: boolean;
}>(
  ({ theme, hasCaption, shouldButtonHaveExternalPadding }) => css`
    // We have to directly select the button element because the Button component applies the
    // className to the wrapping <div> instead of the <button>
    ${!shouldButtonHaveExternalPadding &&
    `&& button {
      min-height: ${
        hasCaption
          ? theme.spacings[7]
          : `calc(${theme.spacings[5]} + ${theme.spacings[2]})`
      };
      padding: 0 calc(${theme.spacings[2]} + ${theme.spacings[1]});
    }`}
    ${shouldButtonHaveExternalPadding &&
    `padding: calc(${theme.spacings[2]} + ${theme.spacings[1]});`}
    
    height: 100%;
    min-height: ${hasCaption
      ? theme.spacings[7]
      : `calc(${theme.spacings[5]} + ${theme.spacings[2]})`};
    width: 100%;
    border-radius: 0 !important;
  `,
);

const LabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
`;

const StyledBodyText = styled(BodyText)<{ isDisabled?: boolean }>(
  ({ isDisabled }) => css`
    ${isDisabled && { ...ActionSheetButtonStyles.disabled }}
  `,
);

const CaptionText = styled(CaptionSecondaryText)(
  ({ theme }) => `
  margin-top: ${theme.spacings[1]};
`,
);

const EmptySearchText = styled.div`
  padding: 0 12px;
`;

const NoItemsContainer = styled.div(
  ({ theme }) => `
  padding: ${theme.spacings[3]};
`,
);

const SearchBarContainer = styled.div(
  ({ theme }) => `
  padding: ${theme.spacings[2]} 12px;
`,
);

const ContentsWrapper = styled.div(
  ({ theme }) => `
  width: ${theme.smallMeasure};
  padding: ${theme.spacings[2]} 0;
  overflow-y: auto;
`,
);

export default ActionSheet;
